import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowUpRight } from "lucide-react";
import PropertyDialog from "@/components/site/PropertyDialog";

const IMAGES = {
  "the-bay":
    "https://static.propsearch.ae/dubai-locations/the-bay_oiDVF_xl.jpg",
  "spinnaker-residence":
    "https://static.propsearch.ae/dubai-locations/spinnaker-residence-23137_xl.jpg",
  "battersea-residence":
    "https://static.propsearch.ae/dubai-locations/battersea-residence_ls2bw_xl.jpg",
  future:
    "https://images.pexels.com/photos/34700467/pexels-photo-34700467.png?auto=compress&cs=tinysrgb&w=1600",
};

export const Portfolio = () => {
  const { t } = useLanguage();
  const items = t.portfolio.items;
  const [openId, setOpenId] = useState(null);

  const handleOpen = (id) => setOpenId(id);
  const handleOpenChange = (open) => {
    if (!open) setOpenId(null);
  };

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="eyebrow mb-6" data-testid="portfolio-eyebrow">
              {t.portfolio.eyebrow}
            </div>
            <h2
              data-testid="portfolio-title"
              className="font-serif-display text-4xl sm:text-5xl md:text-6xl font-light text-[#F8F8F8] leading-tight max-w-2xl"
            >
              {t.portfolio.title}
            </h2>
          </div>
          <div className="hairline w-full md:w-1/3" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {items.map((item, idx) => {
            const span =
              idx === 0
                ? "md:col-span-7"
                : idx === 1
                ? "md:col-span-5"
                : idx === 2
                ? "md:col-span-5"
                : "md:col-span-7";
            const tall =
              idx === 0 || idx === 3
                ? "h-[420px] md:h-[560px]"
                : "h-[420px] md:h-[480px]";
            return (
              <article
                key={item.id}
                data-testid={`portfolio-item-${item.id}`}
                onClick={() => handleOpen(item.id)}
                className={`group col-span-1 ${span} relative overflow-hidden bg-[#141414] border border-white/5 hover:border-[#D4AF37]/40 transition-colors duration-500 cursor-pointer`}
              >
                <div className={`relative ${tall} overflow-hidden`}>
                  <img
                    src={IMAGES[item.id]}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
                </div>

                <div className="absolute top-6 start-6 flex items-center gap-3">
                  <span className="w-6 h-px bg-[#D4AF37]" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
                    {item.tag}
                  </span>
                </div>

                <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h3 className="font-serif-display text-2xl md:text-3xl text-[#F8F8F8] font-light">
                        {item.name}
                      </h3>
                      <div className="text-sm text-[#A1A1AA] mt-1">
                        {item.location}
                      </div>
                    </div>
                    <button
                      data-testid={`portfolio-view-${item.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpen(item.id);
                      }}
                      className="shrink-0 inline-flex items-center justify-center w-11 h-11 border border-white/15 text-[#F8F8F8] hover:bg-[#D4AF37] hover:text-[#0A0A0A] hover:border-[#D4AF37] transition-colors"
                      aria-label={t.portfolio.view}
                    >
                      <ArrowUpRight size={18} />
                    </button>
                  </div>
                  <p className="mt-4 text-sm text-[#A1A1AA] max-w-md leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {item.note}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <PropertyDialog
        propertyId={openId}
        open={!!openId}
        onOpenChange={handleOpenChange}
      />
    </section>
  );
};

export default Portfolio;
